import JWTUtils from './jwt.js';
import API_CONFIG from '../config/api';

// 用户认证工具类
class AuthManager {
  constructor() {
    this.TOKEN_KEY = 'auth_token';
    this.USER_KEY = 'user_info';
  }

  // 保存JWT token
  saveToken(token) {
    try {
      localStorage.setItem(this.TOKEN_KEY, token);
      return true;
    } catch (error) {
      return false;
    }
  }

  // 保存用户信息到本地（用于 /auth/me 不可用时的兜底）
  saveUserInfo(userInfo) {
    try {
      if (!userInfo) return false;
      localStorage.setItem(this.USER_KEY, JSON.stringify(userInfo));
      return true;
    } catch (error) {
      return false;
    }
  }

  // 读取本地缓存的用户信息
  getUserInfoLocal() {
    try {
      const userStr = localStorage.getItem(this.USER_KEY);
      return userStr ? JSON.parse(userStr) : null;
    } catch (error) {
      return null;
    }
  }

  // 通过API获取用户信息
  async getUserFromAPI() {
    try {
      const token = this.getToken();
      if (!token) return null;
      
      // 检查token是否有效
      if (JWTUtils.isTokenExpired(token)) {
        this.logout();
        return null;
      }
      
      // 调用API获取用户信息
      const response = await fetch(`${API_CONFIG.BASE_URL}/api/auth/me`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      });
      
      if (!response.ok) {
        if (response.status === 401) {
          // token无效，清除登录状态
          this.logout();
        }
        return null;
      }
      
      const data = await response.json();
      if (data.success && data.data) {
        return data.data;
      }
      
      return null;
    } catch (error) {
      return null;
    }
  }
  
  // 保持向后兼容的getUser方法
  async getUser() {
    return this.getUserFromAPI();
  }
  
  // 同步获取用户信息（用于不需要等待的场景）
  // 注意：由于API调用是异步的，这个方法现在返回null
  // 建议使用getUserFromAPI()方法
  getUserSync() {
    return null;
  }
  
  // 注意：已移除缓存逻辑，现在通过API获取用户信息

  // 获取token
  getToken() {
    const token = localStorage.getItem(this.TOKEN_KEY);
    return token;
  }

  // 检查是否已登录
  isLoggedIn() {
    const token = this.getToken();
    
    if (!token) {
      return false;
    }
    
    // 检查JWT格式
    if (!JWTUtils.isValidFormat(token)) {
      this.logout();
      return false;
    }
    
    // 检查token是否过期
    if (JWTUtils.isTokenExpired(token)) {
      this.logout();
      return false;
    }
    
    return true;
  }

  // 获取用户显示名称（异步）
  async getDisplayName() {
    const user = await this.getUserFromAPI();
    if (!user) return '游客';
    
    return user.name || user.nickname || `用户${user.id}`;
  }

  // 获取用户头像（异步）
  async getAvatar() {
    const user = await this.getUserFromAPI();
    if (!user) return null;
    
    return user.avatar;
  }

  // 检查用户是否有微信绑定（异步）
  async hasWechat() {
    const user = await this.getUserFromAPI();
    return user && user.wechat_openid;
  }

  // 检查邮箱是否已验证（异步）
  async isEmailVerified() {
    const user = await this.getUserFromAPI();
    return user && user.email_verified_at;
  }

  // 检查手机是否已验证（异步）
  async isPhoneVerified() {
    const user = await this.getUserFromAPI();
    return user && user.phone_verified_at;
  }

  // 更新用户信息（JWT中无法直接更新，需要重新获取token）
  updateUser(updates) {
    return false;
  }

  // 登出
  logout() {
    try {
      localStorage.removeItem(this.TOKEN_KEY);
      localStorage.removeItem(this.USER_KEY);
      return true;
    } catch (error) {
      return false;
    }
  }

  // 获取认证头
  getAuthHeaders() {
    const token = this.getToken();
    return token ? { 'Authorization': `Bearer ${token}` } : {};
  }

  // 检查token是否即将过期（1小时内）
  isTokenExpiringSoon() {
    const token = this.getToken();
    if (!token) return false;
    
    return JWTUtils.isTokenExpiringSoon(token);
  }

  // 刷新token (JWT版本)
  async refreshToken() {
    try {
      const response = await fetch(`${API_CONFIG.BASE_URL}/api/auth/refresh`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...this.getAuthHeaders()
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.success && data.data.token) {
          this.saveToken(data.data.token);
          return true;
        }
      }
      
      return false;
    } catch (error) {
      return false;
    }
  }
}

// 创建单例实例
const authManager = new AuthManager();

export default authManager;
